/**
 * 侧边栏状态管理 Store
 * 管理侧边栏的折叠/展开状态，支持响应式自动调整和持久化
 */

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { defineStore } from 'pinia'

// 断点定义 (单位: px)
const BREAKPOINTS = {
  mobile: 768, // 移动端断点
  tablet: 1199, // 平板断点
  desktop: 1200, // 桌面断点
}

// 存储键名
const STORAGE_KEY = 'sidebar_collapsed'

export const useSidebarStore = defineStore('sidebar', () => {
  // ==================== State ====================

  /** 是否折叠状态 - false: 展开, true: 折叠 */
  const isCollapsed = ref(false)

  /** 是否手动控制（自动模式时只在用户未手动操作时生效） */
  const isManualControlled = ref(false)

  /** 移动端抽屉是否打开 */
  const isMobileDrawerOpen = ref(false)

  /** 当前窗口宽度 */
  const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200)

  // ==================== Getters ====================

  /** 侧边栏宽度 */
  const sidebarWidth = computed(() => {
    if (isCollapsed.value) {
      return 64 // 折叠状态: 只显示图标
    }
    return 240 // 展开状态: 完整宽度
  })

  /** 主内容区左边距 */
  const mainContentMarginLeft = computed(() => {
    return `${sidebarWidth.value}px`
  })

  /** 头部左边距 */
  const headerLeft = computed(() => {
    return `${sidebarWidth.value}px`
  })

  /** 是否为移动端 */
  const isMobile = computed(() => {
    return windowWidth.value <= BREAKPOINTS.mobile
  })

  /** 是否为平板 */
  const isTablet = computed(() => {
    return windowWidth.value > BREAKPOINTS.mobile && windowWidth.value <= BREAKPOINTS.tablet
  })

  /** 是否为桌面端 */
  const isDesktop = computed(() => {
    return windowWidth.value >= BREAKPOINTS.desktop
  })

  /** 是否处于响应式折叠模式 */
  const isResponsiveMode = computed(() => {
    return !isManualControlled.value
  })

  // ==================== Actions ====================

  /**
   * 切换折叠状态
   * 标记为手动控制
   */
  function toggleCollapse() {
    isCollapsed.value = !isCollapsed.value
    isManualControlled.value = true
    saveState()
  }

  /**
   * 设置折叠状态
   * @param collapsed 是否折叠
   * @param manual 是否手动控制（影响持久化和响应式行为）
   */
  function setCollapsed(collapsed: boolean, manual: boolean = false) {
    isCollapsed.value = collapsed
    if (manual) {
      isManualControlled.value = true
      saveState()
    }
  }

  /**
   * 展开侧边栏
   */
  function expand() {
    setCollapsed(false, true)
  }

  /**
   * 折叠侧边栏
   */
  function collapse() {
    setCollapsed(true, true)
  }

  /**
   * 切换移动端抽屉
   */
  function toggleMobileDrawer() {
    isMobileDrawerOpen.value = !isMobileDrawerOpen.value
  }

  /**
   * 打开移动端抽屉
   */
  function openMobileDrawer() {
    if (isMobile.value) {
      isMobileDrawerOpen.value = true
    }
  }

  /**
   * 关闭移动端抽屉
   */
  function closeMobileDrawer() {
    isMobileDrawerOpen.value = false
  }

  /**
   * 响应式自动调整
   * - 移动端(<768px): 自动折叠
   * - 平板(768-1199px): 根据用户偏好或默认展开
   * - 桌面端(≥1200px): 自动展开
   */
  function responsiveAdjust() {
    if (isManualControlled.value) {
      // 手动控制模式下，只调整极端情况
      if (isMobile.value) {
        isCollapsed.value = true
      }
      return
    }

    // 自动响应式模式
    if (isMobile.value) {
      isCollapsed.value = true
    } else if (isTablet.value) {
      isCollapsed.value = true
      isMobileDrawerOpen.value = false
    } else if (isDesktop.value) {
      isCollapsed.value = false
      isMobileDrawerOpen.value = false
    }
  }

  /**
   * 重置为自动模式
   * 清除手动控制标记，根据窗口自动调整
   */
  function resetToAutoMode() {
    isManualControlled.value = false
    responsiveAdjust()
  }

  /**
   * 加载保存的状态
   */
  function loadState() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const state = JSON.parse(saved)
        isCollapsed.value = state.isCollapsed ?? false
        isManualControlled.value = state.isManualControlled ?? false

        // 移动端始终强制折叠，不受持久化影响
        if (isMobile.value) {
          isCollapsed.value = true
        }
      } else {
        // 首次访问：根据屏幕尺寸设置初始状态
        responsiveAdjust()
      }
    } catch {
      // 失败时使用自动模式
      responsiveAdjust()
    }
  }

  /**
   * 保存状态到本地存储
   */
  function saveState() {
    try {
      const state = {
        isCollapsed: isCollapsed.value,
        isManualControlled: isManualControlled.value,
        timestamp: Date.now(),
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {
      // 忽略存储失败
    }
  }

  // ==================== 响应式监听 ====================

  let resizeTimer: ReturnType<typeof setTimeout> | null = null

  function handleResize() {
    windowWidth.value = window.innerWidth

    // 防抖处理
    if (resizeTimer) {
      clearTimeout(resizeTimer)
    }

    resizeTimer = setTimeout(() => {
      responsiveAdjust()
    }, 100)
  }

  // ==================== 初始化 ====================

  // 监听窗口宽度变化
  watch(windowWidth, responsiveAdjust)

  // 监控模式切换
  watch(isCollapsed, () => {
    // 触发界面更新
  })

  // 返回供外部使用的属性和方法
  return {
    // State
    isCollapsed,
    isManualControlled,
    windowWidth,
    isMobileDrawerOpen,

    // Getters
    sidebarWidth,
    mainContentMarginLeft,
    headerLeft,
    isMobile,
    isTablet,
    isDesktop,
    isResponsiveMode,

    // Actions
    toggleCollapse,
    setCollapsed,
    expand,
    collapse,
    toggleMobileDrawer,
    openMobileDrawer,
    closeMobileDrawer,
    responsiveAdjust,
    resetToAutoMode,
    loadState,
    saveState,
    handleResize,
  }
})

// ==================== 组合式函数 ====================

/**
 * 使用侧边栏响应式的组合式函数
 * 在组件中自动初始化和清理
 */
export function useSidebarResponsive() {
  const store = useSidebarStore()

  onMounted(() => {
    store.loadState()
    window.addEventListener('resize', store.handleResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', store.handleResize)
  })

  return store
}
